/**
 * The sampled thumbnails drawn at the shape the recorded size gives them.
 *
 * Display-only. The urls are the ones the players' lobby requests, so what the operator sees here
 * is the shape their players get rather than an approximation of it.
 */
import { useState } from "react";
import { t } from "../../../i18n";
import { AssetSample } from "./assetSample";

/** One thumbnail to draw: the game it stands for and the url it is served from. */
export interface PreviewTile {
  sample: AssetSample;
  url: string;
}

interface Props {
  tiles: PreviewTile[];
  /** Height over width of the style's source images; the shape the lobby gives every tile. */
  ratio: number;
}

export function TilePreview({ tiles, ratio }: Props): JSX.Element | null {
  // A url the style has no asset for is a real state of a half-configured document, and the browser
  // draws a broken-image glyph for it. Dropping the tile keeps the preview to what a player would
  // actually be shown, so a preview with nothing to show is simply absent.
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const available = tiles.filter((tile) => unavailable.indexOf(tile.url) === -1);

  if (available.length === 0) {
    return null;
  }

  const markUnavailable = (url: string): void => {
    setUnavailable((previous) => (previous.indexOf(url) === -1 ? previous.concat(url) : previous));
  };

  return (
    <div
      className="mb-3"
      data-testid="enforced-style-preview"
    >
      <h6>{t("enforced-thumbnail-style.preview-title", undefined, "Tile preview")}</h6>
      <div className="d-flex flex-wrap">
        {available.map(({ sample, url }) => (
          <div
            key={url}
            className="mr-2 mb-2"
            // A fixed width leaves the ratio as the only thing shaping a tile, and Bootstrap 4 has no width utility at this size.
            style={{ width: "9rem" }}
          >
            {/* A padding-top box gives the tile the style's own height-over-width ratio, the same
                ratio the players' lobby shapes its tiles with. */}
            <div
              className="position-relative bg-light"
              data-testid="enforced-style-preview-tile"
              style={{ paddingTop: `${ratio * 100}%` }}
            >
              {/* No object-fit: the initial `fill` is what the players' lobby paints its tiles with,
                  and it shows a recorded size that does not match the asset as distortion instead of
                  cropping the mismatch out of sight. */}
              <img
                src={url}
                alt={sample.game}
                className="position-absolute w-100 h-100"
                // Filling the padded box needs the tile pinned to its corner, and Bootstrap 4 has no inset utility to do it with.
                style={{ top: 0, left: 0 }}
                onError={() => markUnavailable(url)}
              />
            </div>
            <small className="text-muted">{sample.game}</small>
          </div>
        ))}
      </div>
    </div>
  );
}
